import { useRef } from 'react';
import { EbookTemplate } from '../../../mocks/ebookTemplates';

interface Collection {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  color: string;
  borderColor: string;
  templates: EbookTemplate[];
}

interface TemplateCollectionsProps {
  allTemplates: EbookTemplate[];
  onPreview: (t: EbookTemplate) => void;
  favoriteIds: string[];
  onToggleFavorite: (id: string) => void;
}

const BADGE_STYLES: Record<string, string> = {
  Popular: 'bg-amber-100 text-amber-700',
  New: 'bg-emerald-100 text-emerald-700',
  Trending: 'bg-rose-100 text-rose-700',
};

function CollectionCard({
  template,
  onPreview,
  isFavorite,
  onToggleFavorite,
  color,
}: {
  template: EbookTemplate;
  onPreview: (t: EbookTemplate) => void;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
  color: string;
}) {
  return (
    <div
      className="group flex-shrink-0 w-44 bg-white rounded-2xl border-2 border-slate-200 hover:border-amber-400 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer"
      onClick={() => onPreview(template)}
    >
      <div className="relative overflow-hidden" style={{ aspectRatio: '3/4', maxHeight: '152px' }}>
        <img
          src={template.image}
          alt={template.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
        {template.badge && (
          <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold ${BADGE_STYLES[template.badge] || ''}`}>
            {template.badge}
          </div>
        )}
        <button
          onClick={e => { e.stopPropagation(); onToggleFavorite(template.id); }}
          className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center border transition-all cursor-pointer backdrop-blur-sm ${
            isFavorite ? 'bg-rose-500 border-rose-500 text-white' : 'bg-white/90 border-white/50 text-slate-400 hover:text-rose-500'
          }`}
        >
          <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-xs`}></i>
        </button>
        {/* Download count */}
        <div className="absolute bottom-2 left-2 flex items-center gap-1 px-1.5 py-0.5 bg-black/50 backdrop-blur-sm rounded-full">
          <i className="ri-download-line text-white/80 text-xs"></i>
          <span className="text-white/90 text-xs font-semibold">{(template.downloads / 1000).toFixed(1)}k</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: template.color }}></div>
      </div>
      <div className="p-3">
        <h4 className="text-xs font-bold text-slate-900 line-clamp-2 leading-snug mb-2 group-hover:text-amber-700 transition-colors" style={{ minHeight: '2.5rem' }}>
          {template.name}
        </h4>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <i className="ri-star-fill text-amber-400 text-xs"></i>
            <span className="text-xs font-bold text-slate-800">{template.rating}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <i className="ri-book-open-line text-xs"></i>
            <span>{template.chapters}ch</span>
          </div>
        </div>
        <button
          onClick={e => { e.stopPropagation(); onPreview(template); }}
          className="mt-2 w-full py-1.5 rounded-lg text-xs font-bold text-white transition-all whitespace-nowrap opacity-0 group-hover:opacity-100"
          style={{ backgroundColor: color }}
        >
          Preview
        </button>
      </div>
    </div>
  );
}

function CollectionRow({
  collection,
  onPreview,
  favoriteIds,
  onToggleFavorite,
}: {
  collection: Collection;
  onPreview: (t: EbookTemplate) => void;
  favoriteIds: string[];
  onToggleFavorite: (id: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === 'right' ? 360 : -360, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-md"
            style={{ backgroundColor: collection.color }}
          >
            <i className={`${collection.icon} text-base`}></i>
          </div>
          <div>
            <h3 className="text-base font-extrabold text-slate-900">{collection.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{collection.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-slate-400 font-medium hidden sm:block">{collection.templates.length} templates</span>
          <button
            onClick={() => scroll('left')}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 hover:border-slate-300 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-base"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 hover:border-slate-300 transition-all cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-base"></i>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {collection.templates.map(t => (
          <CollectionCard
            key={t.id}
            template={t}
            onPreview={onPreview}
            isFavorite={favoriteIds.includes(t.id)}
            onToggleFavorite={onToggleFavorite}
            color={collection.color}
          />
        ))}
      </div>

      {/* Subtle divider */}
      <div className="h-px bg-slate-100 mt-6"></div>
    </div>
  );
}

export default function TemplateCollections({
  allTemplates,
  onPreview,
  favoriteIds,
  onToggleFavorite,
}: TemplateCollectionsProps) {
  const collections: Collection[] = [
    {
      id: 'beginners',
      title: 'Best for Beginners',
      subtitle: 'Simple, clean layouts — no design skills required',
      icon: 'ri-seedling-line',
      color: '#10b981',
      borderColor: 'border-emerald-100',
      templates: allTemplates
        .filter(t => t.tags.some(tag => ['Clean', 'Minimal', 'Warm', 'Friendly'].includes(tag)) && t.chapters <= 10)
        .sort((a, b) => b.downloads - a.downloads)
        .slice(0, 10),
    },
    {
      id: 'business-coaches',
      title: 'Top for Business Coaches',
      subtitle: 'Trusted by coaches, consultants & thought leaders',
      icon: 'ri-user-star-line',
      color: '#6366f1',
      borderColor: 'border-indigo-100',
      templates: allTemplates
        .filter(t => ['Business & Entrepreneurship', 'Marketing & Sales', 'Career & Leadership'].includes(t.category))
        .sort((a, b) => b.rating - a.rating)
        .slice(0, 10),
    },
    {
      id: 'top-fiction',
      title: 'Most Downloaded Fiction',
      subtitle: 'Narrative & storytelling templates readers love',
      icon: 'ri-quill-pen-line',
      color: '#ec4899',
      borderColor: 'border-pink-100',
      templates: allTemplates
        .filter(t => t.category === 'Fiction & Storytelling')
        .sort((a, b) => b.downloads - a.downloads)
        .slice(0, 10),
    },
    {
      id: 'minimal-clean',
      title: 'Staff Picks: Minimal & Clean',
      subtitle: 'Let your content shine with timeless, clutter-free design',
      icon: 'ri-subtract-line',
      color: '#64748b',
      borderColor: 'border-slate-100',
      templates: allTemplates
        .filter(t => t.tags.includes('Minimal') || t.tags.includes('Clean'))
        .sort((a, b) => b.rating - a.rating)
        .slice(0, 10),
    },
    {
      id: 'trending-tech',
      title: 'Trending in Technology',
      subtitle: 'Hot picks for developers, AI builders & tech educators',
      icon: 'ri-cpu-line',
      color: '#06b6d4',
      borderColor: 'border-cyan-100',
      templates: allTemplates
        .filter(t => t.category === 'Technology & Programming')
        .sort((a, b) => b.downloads - a.downloads)
        .slice(0, 10),
    },
    {
      id: 'editors-choice',
      title: "Editor's Choice",
      subtitle: 'Highest-rated templates hand-picked across all genres',
      icon: 'ri-award-line',
      color: '#f59e0b',
      borderColor: 'border-amber-100',
      templates: allTemplates
        .filter(t => t.rating >= 4.8)
        .sort((a, b) => b.rating - a.rating)
        .slice(0, 10),
    },
  ].filter(c => c.templates.length >= 3);

  if (collections.length === 0) return null;

  return (
    <section className="bg-white border-t border-slate-200 py-14">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="flex items-center gap-4 mb-10">
          <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-200">
            <i className="ri-folder-star-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-extrabold text-slate-900">Template Collections</h2>
            <p className="text-slate-500 text-sm mt-0.5">Hand-curated sets for every goal, audience, and writing style</p>
          </div>
          <div className="ml-auto hidden md:flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl">
            <i className="ri-sparkling-line text-amber-500 text-sm"></i>
            <span className="text-xs font-bold text-amber-700">{collections.reduce((acc, c) => acc + c.templates.length, 0)}+ curated templates</span>
          </div>
        </div>

        <div className="space-y-10">
          {collections.map(col => (
            <CollectionRow
              key={col.id}
              collection={col}
              onPreview={onPreview}
              favoriteIds={favoriteIds}
              onToggleFavorite={onToggleFavorite}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
